import { useMemo } from 'react';

import { useChatContextPageSmartLinks } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	type ConnectorState,
	useEnabledKnowledgeSources,
} from '@atlassian/conversation-assistant-store/controllers/connectors-store';

/**
 * Extracts the `serviceKey` query parameter from an outbound auth URL.
 * Handles two URL shapes:
 * - Direct: `outboundAuth/start?serviceKey=slack&...`
 * - Wrapped: `login?continue=...%2FoutboundAuth%2Fstart%3FserviceKey%3Dslack...`
 *
 * Both the ORS `meta.auth[0].url` and the connector `outboundAuthUrl` use the same
 * `serviceKey` value, making it the reliable key for cross-environment matching.
 */
function extractServiceKey(url: string): string | undefined {
	try {
		const parsed = new URL(url);
		const direct = parsed.searchParams.get('serviceKey');
		if (direct) {
			return direct;
		}
		const continueParam = parsed.searchParams.get('continue');
		if (continueParam) {
			const inner = new URL(decodeURIComponent(continueParam));
			return inner.searchParams.get('serviceKey') ?? undefined;
		}
	} catch {
		// malformed URL
	}
	return undefined;
}

/**
 * Reads the SmartLink connector context from the store and returns the first
 * unconnected ConnectorState matching a detected SmartLink provider.
 *
 * This hook is purely a reader — it does not publish any events.
 * The subscription signal is managed separately by useSmartLinkSubscription.
 *
 * @returns The first unconnected ConnectorState matching a detected SmartLink, or null
 */
export const useSmartLinkConnector = (): ConnectorState | null => {
	const [pageSmartLinks] = useChatContextPageSmartLinks();
	const [allKnowledgeSources] = useEnabledKnowledgeSources();

	return useMemo(() => {
		if (!pageSmartLinks?.length || !allKnowledgeSources.length) {
			return null;
		}

		// Primary: match by friendlyName (case-insensitive) — works regardless of auth state
		const unconnectedByName = new Map<string, ConnectorState>();
		// Fallback: match by serviceKey extracted from outboundAuthUrl
		const unconnectedByServiceKey = new Map<string, ConnectorState>();

		for (const connector of allKnowledgeSources) {
			if (!connector.isUserOAuthed) {
				if (connector.friendlyName) {
					unconnectedByName.set(connector.friendlyName.toLowerCase(), connector);
				}
				const serviceKey = extractServiceKey(connector.outboundAuthUrl) ?? connector.source;
				unconnectedByServiceKey.set(serviceKey, connector);
			}
		}

		if (unconnectedByName.size === 0 && unconnectedByServiceKey.size === 0) {
			return null;
		}

		// pageSmartLinks is sorted by count desc — first match wins
		for (const smartLink of pageSmartLinks) {
			// Primary: match generatorName against connector friendlyName
			if (smartLink.generatorName) {
				const connector = unconnectedByName.get(smartLink.generatorName.toLowerCase());
				if (connector) {
					return connector;
				}
			}
			// Fallback: match orsAuthKey against connector serviceKey
			if (smartLink.orsAuthKey) {
				const connector = unconnectedByServiceKey.get(smartLink.orsAuthKey);
				if (connector) {
					return connector;
				}
			}
		}

		return null;
	}, [pageSmartLinks, allKnowledgeSources]);
};
